import { logger } from '@heidi/runtime/logger';

import {
  CAPTURE_WORKLET_NAME,
  CAPTURE_WORKLET_URL,
  VOICE_SAMPLE_RATE,
} from './constants';

interface PcmRecorderOptions {
  deviceId?: string;
  onFrame: (pcm: ArrayBuffer) => void;
  onError?: (message: string) => void;
  /**
   * Raised specifically when microphone capture is blocked by a denied
   * permission (as opposed to a missing device or a graph failure), so the
   * caller can drive a platform-aware recovery state instead of a generic
   * error line.
   */
  onPermissionDenied?: () => void;
}

/** A denied getUserMedia permission surfaces as one of these DOMException names. */
const isMicPermissionDenied = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === 'NotAllowedError' || error.name === 'SecurityError');

/**
 * Captures microphone audio as PCM16 mono 24 kHz frames via an AudioWorklet,
 * matching the OpenAI Realtime input format. Frames are delivered to
 * `onFrame` as transferred ArrayBuffers; the caller streams them over the WS.
 *
 * Also exposes the live `MediaStream` so the modal can drive the orb
 * visualiser from the same input without opening a second mic.
 */
export class PcmRecorder {
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private readonly onFrame: (pcm: ArrayBuffer) => void;
  private readonly onError?: (message: string) => void;
  private readonly onPermissionDenied?: () => void;
  private readonly deviceId?: string;

  constructor(options: PcmRecorderOptions) {
    this.onFrame = options.onFrame;
    this.onError = options.onError;
    this.onPermissionDenied = options.onPermissionDenied;
    this.deviceId = options.deviceId;
  }

  get stream(): MediaStream | null {
    return this.mediaStream;
  }

  async start(): Promise<boolean> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: {
          deviceId: this.deviceId || 'default',
          channelCount: 1,
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (error) {
      if (isMicPermissionDenied(error)) {
        logger.error('[voice] microphone permission denied', error);
        this.onPermissionDenied?.();
        return false;
      }
      logger.error('[voice] failed to acquire the microphone', error);
      this.onError?.('Could not access the microphone. Please try again.');
      return false;
    }

    try {
      this.audioContext = new AudioContext({ sampleRate: VOICE_SAMPLE_RATE });
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      await this.audioContext.audioWorklet.addModule(CAPTURE_WORKLET_URL);

      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioContext, CAPTURE_WORKLET_NAME);
      this.workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        this.onFrame(event.data);
      };
      // Connect to destination to keep the graph alive; the worklet emits no
      // audio so this is silent.
      this.source.connect(this.workletNode).connect(this.audioContext.destination);
      return true;
    } catch (error) {
      logger.error('[voice] failed to initialise capture graph', error);
      this.onError?.('Failed to initialise the microphone.');
      this.stop();
      return false;
    }
  }

  setMuted(muted: boolean): void {
    this.workletNode?.port.postMessage({ muted });
  }

  stop(): void {
    try {
      this.mediaStream?.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      if (this.workletNode) {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
        this.workletNode.port.close();
      }
      this.source?.disconnect();
      if (this.audioContext && this.audioContext.state !== 'closed') {
        void this.audioContext.close();
      }
    } catch (error) {
      logger.debug('[voice] error during recorder teardown', error);
    } finally {
      this.mediaStream = null;
      this.audioContext = null;
      this.workletNode = null;
      this.source = null;
    }
  }
}
